<div class="sr-only" role="status" aria-live="polite">
  {{ screenReaderAnnouncement }}
</div>

@if (contactService.showSuccessMessage()) {
<div class="success-message" role="alert" aria-live="polite">
  <h2
    tabindex="0"
    i18n="@@home.contact-form.successTitle"
    data-testid="contact-success-title"
  >
    Vielen Dank für Ihre Nachricht!
  </h2>
  <p
    tabindex="0"
    i18n="@@home.contact-form.successMessage"
    data-testid="contact-success-message"
  >
    Wir haben Ihre Anfrage erhalten und werden uns schnellstmöglich bei
    Ihnen melden.
  </p>
  <button
    mat-raised-button
    color="primary"
    (click)="back()"
    data-testid="contact-back-button"
    i18n-aria-label="@@home.contact-form.backToHomeAriaLabel"
    aria-label="Zurück zur Startseite"
    i18n="@@home.contact-form.backToHomeButton"
  >
    Zurück zur Startseite
  </button>
</div>
} @else {
<form
  [formGroup]="contactForm"
  (ngSubmit)="onSubmit()"
  class="contact-form"
  data-testid="contact-form"
  i18n-aria-label="@@home.contact-form.formAriaLabel"
  aria-label="Kontaktformular"
  novalidate
>
  <div class="form-row">
    <div class="email-field-wrapper">
      <mat-form-field appearance="outline" class="form-field" color="accent">
        <mat-label id="email-label" i18n="@@common.email">E-Mail</mat-label>
        <input
          matInput
          formControlName="email"
          data-testid="contact-email"
          i18n-aria-label="@@home.contact-form.emailInputAriaLabel"
          [attr.aria-label]="emailInputAriaLabel"
          [attr.aria-required]="true"
          [attr.aria-describedby]="emailErrorId"
          i18n-placeholder="@@home.contact-form.emailPlaceholder"
          [placeholder]="emailPlaceholder"
          type="email"
          autocomplete="email"
          [readonly]="loggedInUserEmail() !== null"
          (blur)="emailHint.check()"
          (focus)="emailHint.reset()"
        />
        <lib-form-error
          [formGroup]="contactForm"
          controlName="email"
          validationKey="email"
          [attr.id]="emailErrorId"
        >
        </lib-form-error>
      </mat-form-field>
      <lib-email-suggestion-hint
        #emailHint
        [email]="$safeNavigationMigration(contactForm.get('email')?.value)"
        (applied)="contactForm.get('email')?.setValue($event)"
      ></lib-email-suggestion-hint>
    </div>

    <mat-form-field appearance="outline" class="form-field" color="accent">
      <mat-label id="name-label" i18n="@@home.contact-form.nameLabel"
        >Name</mat-label
      >
      <input
        matInput
        formControlName="name"
        data-testid="contact-name"
        i18n-aria-label="@@home.contact-form.nameInputAriaLabel"
        [attr.aria-label]="nameInputAriaLabel"
        [attr.aria-required]="true"
        [attr.aria-describedby]="nameErrorId"
        i18n-placeholder="@@home.contact-form.namePlaceholder"
        [placeholder]="namePlaceholder"
        autocomplete="name"
      />
      <lib-form-error
        [formGroup]="contactForm"
        controlName="name"
        validationKey="name"
        [attr.id]="nameErrorId"
      >
      </lib-form-error>
    </mat-form-field>
  </div>

  <div>
    <mat-form-field appearance="outline" class="form-field" color="accent">
      <mat-label id="topic-label" i18n="@@home.contact-form.topicLabel"
        >Kategorie</mat-label
      >
      <mat-select
        formControlName="category"
        data-testid="contact-category"
        i18n-aria-label="@@home.contact-form.topicSelectAriaLabel"
        [attr.aria-label]="categorySelectAriaLabel"
        [attr.aria-required]="true"
        [attr.aria-describedby]="topicErrorId"
        i18n-placeholder="@@home.contact-form.topicPlaceholder"
        [placeholder]="categoryPlaceholder"
      >
        @for (topic of topics; track topic.value) {
        <mat-option [value]="topic.value">{{ topic.label }}</mat-option>
        }
      </mat-select>
      <lib-form-error
        [formGroup]="contactForm"
        controlName="category"
        validationKey="other"
        [attr.id]="topicErrorId"
      >
      </lib-form-error>
    </mat-form-field>
  </div>

  <fieldset class="school-section" data-testid="contact-school-section">
    @if (schoolMode() === 'loading') {
    <div class="school-loading" role="status" data-testid="contact-school-loading">
      <mat-spinner diameter="20" aria-hidden="true"></mat-spinner>
      <span i18n="@@home.contact-form.schoolLoading">Schulauswahl wird geladen …</span>
    </div>
    } @else if (schoolMode() === 'children') {
    <legend class="school-legend" i18n="@@home.contact-form.subjectLegend">
      Worum geht es?
    </legend>
    <div class="subject-cards" data-testid="contact-subject">
      @for (child of childOptions(); track child.value) {
      <label
        class="subject-card"
        [class.selected]="selectedSubject() === child.value"
        data-testid="contact-subject-child"
      >
        <input
          type="radio"
          class="subject-radio"
          formControlName="subject"
          [value]="child.value"
        />
        <span class="subject-text">
          <span class="subject-title">{{ child.fullName }}</span>
          @if (child.schoolLabel) {
          <span class="subject-detail">{{ child.schoolLabel }}</span>
          }
        </span>
      </label>
      }
      <label
        class="subject-card general"
        [class.selected]="selectedSubject() === subjectGeneral"
        data-testid="contact-subject-general"
      >
        <input
          type="radio"
          class="subject-radio"
          formControlName="subject"
          [value]="subjectGeneral"
        />
        <span class="subject-text">
          <span class="subject-title" i18n="@@home.contact-form.subjectGeneral"
            >Allgemeine Frage</span
          >
          <span
            class="subject-detail"
            i18n="@@home.contact-form.subjectGeneralDetail"
            >betrifft kein bestimmtes Kind</span
          >
        </span>
      </label>
    </div>
    <button
      type="button"
      class="other-school-link"
      [class.selected]="selectedSubject() === subjectOtherSchool"
      [attr.aria-pressed]="selectedSubject() === subjectOtherSchool"
      data-testid="contact-subject-other-school"
      (click)="chooseOtherSchool()"
      i18n="@@home.contact-form.subjectOtherSchoolLink"
    >
      Anfrage betrifft eine andere Schule …
    </button>
    @if (subjectControl.touched && subjectControl.hasError('required')) {
    <mat-error class="subject-error" i18n="@@home.contact-form.subjectRequired"
      >Bitte wählen Sie aus, worum es geht.</mat-error
    >
    }
    @if (showChildSchoolSearch()) {
    @if (childWithoutSchool()) {
    <p class="subject-detail" i18n="@@home.contact-form.childWithoutSchool">
      Für dieses Kind ist keine Schule hinterlegt – bitte wählen Sie sie aus.
    </p>
    }
    <lib-school-autocomplete
      class="school-search"
      [control]="schoolControl"
      [schools]="schools()"
      [hint]="schoolHint"
      [requiredMessage]="schoolRequiredMessage"
      testId="contact-school"
    />
    }
    } @else {
    <lib-school-autocomplete
      class="school-search"
      [control]="schoolControl"
      [schools]="schools()"
      [hint]="schoolHint"
      [requiredMessage]="schoolRequiredMessage"
      [generalOptionLabel]="generalOptionLabel"
      (generalSelected)="onGeneralSelected()"
      testId="contact-school"
    />
    <mat-checkbox
      formControlName="noSchool"
      data-testid="contact-no-school"
      i18n="@@home.contact-form.noSchoolCheckbox"
      >Meine Anfrage betrifft keine bestimmte Schule</mat-checkbox
    >
    }
  </fieldset>

  @if (loggedInUserEmail() === null) {
  <mat-form-field appearance="outline" class="form-field" color="accent">
    <mat-label i18n="@@home.contact-form.referenceNumberLabel"
      >Antragsnummer</mat-label
    >
    <span matTextPrefix class="reference-prefix">REG-</span>
    <input
      matInput
      formControlName="referenceNumber"
      data-testid="contact-reference-number"
      maxlength="6"
      i18n-aria-label="@@home.contactForm.referenceNumberInputAriaLabel"
      [attr.aria-label]="referenceNumberInputAriaLabel"
      i18n-placeholder="@@home.contactForm.referenceNumberPlaceholder"
      [placeholder]="referenceNumberPlaceholder"
      (paste)="onReferenceNumberPaste($event)"
      (blur)="onReferenceNumberBlur()"
    />
    @if (
      referenceNumberControl?.touched &&
      referenceNumberControl?.hasError('referenceNumber')
    ) {
    <mat-error i18n="@@home.contact-form.referenceNumberError"
      >Bitte geben Sie die 6-stellige Antragsnummer aus Ihrer
      Bestätigungsmail ein (z. B. AB23CD).</mat-error
    >
    } @else {
    <mat-hint i18n="@@home.contact-form.referenceNumberHint"
      >Bei Fragen zur Anmeldung Ihres Kindes geben Sie bitte die Antragsnummer
      aus Ihrer Bestätigungsmail an.</mat-hint
    >
    }
  </mat-form-field>
  }

  <mat-form-field appearance="outline" class="form-field" color="accent">
    <mat-label id="message-label" i18n="@@home.contact-form.messageLabel"
      >Nachricht</mat-label
    >
    <textarea
      matInput
      formControlName="message"
      data-testid="contact-message"
      i18n-aria-label="@@home.contact-form.messageInputAriaLabel"
      [attr.aria-label]="messageInputAriaLabel"
      [attr.aria-required]="true"
      [attr.aria-describedby]="messageErrorId"
      rows="4"
      i18n-placeholder="@@home.contact-form.messagePlaceholder"
      [placeholder]="messagePlaceholder"
    >
    </textarea>
    <lib-form-error
      [formGroup]="contactForm"
      controlName="message"
      validationKey="other"
      [attr.id]="messageErrorId"
    >
    </lib-form-error>
  </mat-form-field>

  <!--
    Hinweis auf passende FAQ-Fragen (#1071). Der Bereich steht dauerhaft im
    DOM, damit aria-live die eingeblendete Karte auch wirklich ansagt; der
    Fokus bleibt dabei im Nachrichtenfeld. Absenden ist nie blockiert.
  -->
  <div class="faq-hint-region" aria-live="polite">
    @if (showHintCard()) {
    <aside class="faq-hint-card" data-testid="contact-faq-hint">
      <mat-icon class="faq-hint-icon" aria-hidden="true">lightbulb</mat-icon>
      <div class="faq-hint-body">
        @if (showCallbackHint()) {
        <p class="faq-hint-text" i18n="@@home.contact-form.faqHint.callback">
          Bitte beschreiben Sie kurz Ihr Anliegen — dann können wir gezielt
          helfen.
        </p>
        } @if (hintLinks().length > 0) {
        <p class="faq-hint-text" i18n="@@home.contact-form.faqHint.intro">
          Vielleicht hilft eine dieser Antworten schon weiter:
        </p>
        <ul class="faq-hint-links">
          @for (link of hintLinks(); track link.faqId) {
          <li>
            <a
              [routerLink]="['/faq']"
              [fragment]="link.faqId"
              target="_blank"
              rel="noopener"
              data-testid="contact-faq-hint-link"
              >{{ link.question }}</a
            >
          </li>
          }
        </ul>
        <p class="faq-hint-note" i18n="@@home.contact-form.faqHint.note">
          Die Antwort öffnet sich in einem neuen Tab — Ihre Nachricht bleibt
          erhalten und kann trotzdem abgeschickt werden.
        </p>
        }
      </div>
      <button
        mat-icon-button
        type="button"
        class="faq-hint-dismiss"
        (click)="dismissHints()"
        data-testid="contact-faq-hint-dismiss"
        i18n-aria-label="@@home.contact-form.faqHint.dismissAriaLabel"
        aria-label="Hinweis ausblenden"
      >
        <mat-icon aria-hidden="true">close</mat-icon>
      </button>
    </aside>
    }
  </div>

  <!--
    Privacy-Consent vorerst deaktiviert (AI ist im ersten Rollout aus,
    daher kein zusätzlicher Consent nötig). Bei AI-Reaktivierung wieder
    einblenden und privacyConsent-Form-Control in contact-form.component.ts
    re-aktivieren.
  <div class="privacy-consent">
    <mat-checkbox
      formControlName="privacyConsent"
      data-testid="contact-privacy-consent"
      color="primary"
    >
      <span i18n="@@home.contact-form.privacyConsent">
        Ich habe die
        <a routerLink="/legal/privacy-policy" target="_blank">Datenschutzerklärung</a>
        gelesen und bin mit der Verarbeitung meiner Daten einverstanden.
      </span>
    </mat-checkbox>
    @if (contactForm.get('privacyConsent')?.touched && contactForm.get('privacyConsent')?.hasError('required')) {
      <mat-error i18n="@@home.contact-form.privacyConsentRequired">
        Bitte stimmen Sie der Datenschutzerklärung zu.
      </mat-error>
    }
  </div>
  -->

  <div class="form-actions">
    <button
      mat-flat-button
      type="submit"
      data-testid="contact-submit"
      [disabled]="loadingService.loading()"
      [attr.aria-busy]="loadingService.loading()"
      aria-label="Formular absenden"
      i18n-aria-label="@@home.contactForm.submitFormAriaLabel"
    >
      <div class="button-content">
        <span i18n="@@home.contact-form.sendMessageButton"
          >Nachricht senden</span
        >
        @if (loadingService.loading()) {
        <mat-spinner
          diameter="20"
          i18n-aria-label="@@home.contact-form.loadingSpinnerAriaLabel"
          aria-label="Laden"
        >
        </mat-spinner>
        }
      </div>
    </button>
  </div>
</form>
}
